import React, { useEffect, useMemo } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { shallowEqual, useSelector } from 'react-redux';

import IMAGES from '../../constants/Images';
import { popUpClick, popUpView } from '../../utils/trackingUtil';
import { POPUP_NAME } from '../../constants/Constants';
import {
  WebcamErrorBlockingWrap,
  Header,
  HeaderIcon,
  Title,
  SubTitle,
  CountdownCard,
  CountdownLabel,
  CountdownTime,
  CountdownHint,
  Checklist,
  ChecklistItem,
} from './WebcamErrorBlockingModal.style';

function pad2(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${pad2(mm)} : ${pad2(ss)}`;
}

export default function WebcamErrorBlockingModal({
  isOpen,
  remainingSeconds,
  isSecureBrowserEnable,
  onRefresh,
  onExit,
}) {
  const { testTypeVerbose } = useSelector(
    state => ({
      testTypeVerbose: state.test?.test_type_verbose ?? 'assessment',
    }),
    shallowEqual
  );

  const remainingLabel = useMemo(
    () =>
      formatMMSS(typeof remainingSeconds === 'number' ? remainingSeconds : 0),
    [remainingSeconds]
  );

  // Track view when opened
  useEffect(() => {
    if (!isOpen) return;
    popUpView({
      payload: {
        popUpTitle: POPUP_NAME.webcamNotDetected,
        popUpAction: 'Webcam error blocking modal',
      },
    });
  }, [isOpen]);

  const handleRefresh = () => {
    popUpClick({
      payload: {
        popUpTitle: POPUP_NAME.webcamNotDetected,
        popUpAction: 'Webcam error blocking modal',
        label: 'refresh',
      },
    });
    if (typeof onRefresh === 'function') onRefresh();
  };

  const handleExit = () => {
    popUpClick({
      payload: {
        popUpTitle: POPUP_NAME.webcamNotDetected,
        popUpAction: 'Webcam error blocking modal',
        label: 'exit',
      },
    });
    if (typeof onExit === 'function') onExit();
  };

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      backdropClassName="webcam-error-blocking-backdrop"
      keyboard={false}
      centered
      size="lg"
    >
      <ModalBody>
        <WebcamErrorBlockingWrap>
          <Header>
            <HeaderIcon
              src={IMAGES.WEBCAM_NOT_FOUND}
              alt="webcam-not-detected"
            />
            <Title>Webcam Not Detected!</Title>
            <SubTitle>
              Please check your settings and click on Refresh.
            </SubTitle>
          </Header>

          <CountdownCard>
            <CountdownLabel>{`${testTypeVerbose} will be auto-submitted in`}</CountdownLabel>
            <CountdownTime>{remainingLabel}</CountdownTime>
            <CountdownHint>seconds if camera is not detected</CountdownHint>
          </CountdownCard>

          <Checklist>
            <ChecklistItem>
              Check if your camera permission is enabled.
            </ChecklistItem>
            <ChecklistItem>
              Ensure no other application (Zoom, Teams) is using the camera.
            </ChecklistItem>
            <ChecklistItem>
              Check browser has permissions for camera use
            </ChecklistItem>
            <ChecklistItem>
              If the issue persists, refresh the page
            </ChecklistItem>
          </Checklist>
        </WebcamErrorBlockingWrap>
      </ModalBody>

      <ModalFooter className="justify-content-center">
        <button
          type="button"
          onClick={handleRefresh}
          className="btn btn-primary"
        >
          Refresh
        </button>
        {isSecureBrowserEnable && (
          <button
            type="button"
            onClick={handleExit}
            className="btn btn-outline-primary ml-3"
          >
            Exit
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
